import React, { useCallback, useEffect, useRef } from 'react'
import { Link } from 'gatsby'
import * as style from './index.module.css'
import {
  ALL_ROUTE,
  HEADER_ROUTES,
  CONPANY_NAME,
  ENTRY_LINK,
  SOZO_WHITE_BOX_ROUTES,
  PORTFOLIO_ROUTES,
} from '@/utils/const'
import { CtaButton } from '@/components/atoms/entrypoint'
import { StaticImage } from 'gatsby-plugin-image'
import { fireEvent } from '@/utils/activityLog'
import { useGetCurrentPath } from '@/utils/hooks/useGetCurrentPath'
import { navigate } from 'gatsby'

interface Props {
  /** メニューが開いているかどうか */
  readonly open: boolean
  /** メニューの開閉 */
  readonly onClick: () => void
}

/**
 * @file SPで使用するHeader
 */
export const SpHeader = ({ open, onClick }: Props): React.JSX.Element => {
  const { currentPath } = useGetCurrentPath()
  const headerRef = useRef<HTMLDivElement>(null) // ヘッダーの参照を保持

  const onClickRoot = useCallback((): void => {
    fireEvent({
      event: 'clickHeaderMenu',
      parameters: {
        current_path: currentPath,
        next_path: ALL_ROUTE[0].route,
      },
    })
  }, [currentPath])

  const onClickEntry = useCallback((): void => {
    fireEvent({
      event: 'clickHeaderEntryButton',
      parameters: {
        current_path: currentPath,
      },
    })
    navigate(ENTRY_LINK)
  }, [currentPath])

  const onClickHeaderMenu = useCallback(
    (route: string): void => {
      fireEvent({
        event: 'clickHeaderMenu',
        parameters: {
          current_path: currentPath,
          next_path: route,
        },
      })
    },
    [currentPath],
  )

  // 外部クリックを検出してメニューを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        headerRef.current &&
        !headerRef.current.contains(event.target as Node)
      ) {
        onClick() // ヘッダーを閉じる処理を実行
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside) // リスナー登録
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside) // クリーンアップ
    }
  }, [open, onClick])

  return (
    <header aria-label="ヘッダー" className={style.root} ref={headerRef}>
      {open && (
        <div className={style.headerArea}>
          <div className={style.companyNameArea}>
            <StaticImage
              src="../../../../images/logo.webp"
              loading="eager"
              width={42}
              height={42}
              formats={['auto', 'webp', 'avif']}
              alt="logo"
            />
            <Link
              to={ALL_ROUTE[0].route}
              aria-label="会社名"
              className={style.companyName}
              onClick={onClickRoot}
            >
              {CONPANY_NAME}
            </Link>
          </div>
          <nav aria-label="ヘッダーナビゲーション" className={style.navList}>
            <a
              className={style.navContainer}
              href={SOZO_WHITE_BOX_ROUTES.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`リンク: ${SOZO_WHITE_BOX_ROUTES.text}`}
            >
              {SOZO_WHITE_BOX_ROUTES.text}
            </a>
            {HEADER_ROUTES.map((list, index) => (
              <Link
                key={index}
                className={style.navContainer}
                aria-label={`リンク: ${list.text}`}
                to={list.route}
                onClick={() => onClickHeaderMenu(list.route)}
              >
                <span>{list.text}</span>
              </Link>
            ))}
            <div className={style.ctaButton}>
              <CtaButton type="whiteTextEntry" onClick={onClickEntry} />
            </div>
          </nav>
        </div>
      )}
      <button onClick={onClick} className={style.button}>
        <span className={style.text}>{open ? 'Close' : 'Menu'}</span>
        {open ? (
          <StaticImage
            src={'../../../../images/icon/close-color-white.svg'}
            loading="eager"
            formats={['auto', 'webp', 'avif']}
            alt="クローズアイコン"
            className={style.icon}
          />
        ) : (
          <StaticImage
            src={'../../../../images/icon/menu-color-white.svg'}
            loading="eager"
            formats={['auto', 'webp', 'avif']}
            alt="アイコン"
            className={style.icon}
          />
        )}
      </button>
    </header>
  )
}
